import React, { useCallback, useState } from 'react';
import { Modal, ModalVariant, Button } from '@patternfly/react-core';

import './customization-page.css';
import CustomizationNameField, { defaultDuplicatedModelNameValidation, validateDuplicatedModelName } from './CustomizationNameField';
import { ModelCustomizationBase, SegmentationModel } from '../../store/model-customization-types';
import { renameCustomizationModel } from '../../store/reducers';
import { useDispatch, useSelector } from 'react-redux';
import { selectAllCustomizationNamesAndParentModelIds } from '../../store/selectors';
import { useTranslation } from 'react-i18next';

interface RenameCustomizationModalProps {
    isOpen: boolean,
    onClose: () => void,
    model: SegmentationModel,
    customization: ModelCustomizationBase,
}

const RenameCustomizationModal = (props: RenameCustomizationModalProps) => {
    const { isOpen, onClose, model, customization } = props;

    const dispatch = useDispatch();
    const { t } = useTranslation();

    const [newNameValidation, setNewNameValidation] = useState(defaultDuplicatedModelNameValidation);
    const [newCustomizationName, setNewCustomizationName] = useState('');

    // needed for duplicated model name validation
    const allCustomizationNamesAndModelIds = useSelector(selectAllCustomizationNamesAndParentModelIds);

    const handleCustomizationRenameChange = useCallback((newName: string) => {
        setNewCustomizationName(newName);
        const validationResult = validateDuplicatedModelName(allCustomizationNamesAndModelIds, newName, model.id);
        setNewNameValidation(validationResult);
    }, [allCustomizationNamesAndModelIds, model.id]);

    const handleConfirmRename = useCallback(() => {
        dispatch(renameCustomizationModel(customization.id, newCustomizationName));
        onClose();
    }, [dispatch, customization.id, newCustomizationName, onClose]);

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={t('dialog.renameCustomization')}
            variant={ModalVariant.medium}
            actions={[
                <Button key="confirm-rename" variant="primary" onClick={handleConfirmRename} isDisabled={newNameValidation.validated === 'error' || !newCustomizationName}>{t('common.rename')}</Button>,
                <Button key="cancel-rename" variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>,
            ]}
        >
            <div>
                <CustomizationNameField
                    segmentationModelName={model.modelName}
                    sourceCustomizationName={customization.customizationName}
                    customizationNameValidation={newNameValidation}
                    onCustomizationNameChange={handleCustomizationRenameChange}
                    customizationName={newCustomizationName}
                />
            </div>
        </Modal>
    );
}

export default RenameCustomizationModal;
